// BUILD-039 Deactivate / Reactivate — the plain-language rules the Manage panel shows. Pure logic only (no React, no network), tested
// under plain Node via type stripping (see lifecycleModel.test.mjs). The SERVER decides who may act and what happened; this file only
// words what it returned, and it never reports success the server did not report.

export type Kind = 'deactivate' | 'reactivate';
export type Tone = 'ok' | 'info' | 'warn' | 'error';

export interface LoginLike {
  app: string; account: string; discoveredLate?: boolean;
  disable: string | null; signOut: string | null; restore: string | null; restoreReason?: string | null; detail?: string | null;
  /** Set when a request to the sign-in provider was sent for this login and no answer was received (the action name, or 'unknown'). */
  unsettled?: string | null;
}
export interface StatusLike {
  globalStatus: string | null;
  operation: { kind: string; state: string; reason?: string } | null;
  open: boolean;
  workerActive?: boolean;
  retry?: { available: boolean; operationId?: string; reason?: string; since?: string };
  /**
   * Provider requests that were sent and never answered. They may still take effect at ANY later time, whatever the database or the
   * provider currently shows, so neither time nor an observed state settles them. Only the same request's own answer, or a Platform
   * Owner's written acknowledgement, ends the hold. `reviewAfterSeconds` is only the minimum age before that review is accepted.
   */
  unconfirmed?: { since: string; reviewAfterSeconds?: number; calls?: Array<{ loginId?: string; app: string; action: string | null; since?: string; reviewableAt?: string; reviewable?: boolean }> } | null;
  /** Direct account actions (disable / delete / demotion) on this person that have not reported their result. */
  authorityReservations?: Array<{ id: string; route: string; since: string }> | null;
  enforcement?: { inEffect: boolean; secondsRemaining: number; windowSeconds: number } | null;
  logins?: LoginLike[];
  discoveredLogins?: Array<{ app: string; account: string }>;
}

export const REASON_MAX = 500;

export function appLabel(app: string): string {
  return app === 'hq' ? 'Unique HQ' : app === 'pricing' ? 'Pricing' : app === 'pm' ? 'Project Manager' : app;
}

/** Which action the panel offers. Inactive is ONLY globalStatus === 'disabled' (the same rule the Directory uses). */
export function offeredAction(globalStatus: string | null | undefined): Kind | null {
  if (globalStatus === 'active') return 'deactivate';
  if (globalStatus === 'disabled') return 'reactivate';
  return null; // unknown status: offer nothing rather than guess
}

export function validateReason(raw: string): { ok: true; reason: string } | { ok: false; message: string } {
  const reason = (raw || '').trim();
  if (!reason) return { ok: false, message: 'A short reason is required.' };
  if (reason.length > REASON_MAX) return { ok: false, message: `Keep the reason to ${REASON_MAX} characters or fewer.` };
  return { ok: true, reason };
}

export function confirmCopy(kind: Kind, name: string): { title: string; body: string; button: string } {
  if (kind === 'deactivate') {
    return {
      title: `Deactivate ${name}?`,
      body: `${name} will no longer be able to use Unique HQ, Pricing or Project Manager, and their open sessions are signed out. Their employee record, documents, HR history, audit trail, roles and app access settings are kept. This is not termination and nothing is deleted.`,
      button: 'Deactivate',
    };
  }
  return {
    title: `Reactivate ${name}?`,
    body: `${name} is shown as active again. Only sign-ins that the deactivation itself turned off are turned back on. A sign-in that was already disabled, was removed, or cannot be proven to be ours stays off. App access settings are not changed, no accounts are created, and no invitation or password is sent.`,
    button: 'Reactivate',
  };
}

export function stateHeadline(s: StatusLike | null | undefined): { tone: Tone; text: string } | null {
  const op = s && s.operation;
  if (!op) return null;
  const verb = op.kind === 'reactivate' ? 'Reactivation' : 'Deactivation';
  // A sent-but-unanswered provider request may still take effect, so the change is never shown as finished while one exists.
  if (s && s.unconfirmed && (op.state === 'running' || op.state === 'incomplete' || op.state === 'complete' || op.state === 'completed-with-exceptions')) {
    return { tone: 'warn', text: `${verb} is NOT finished. A request to the sign-in provider was sent and no answer came back, so it may still take effect at any time. Waiting does not clear it. Nothing is repeated, reversed or turned back on until a Platform Owner has reviewed it.` };
  }
  switch (op.state) {
    case 'running': return { tone: 'info', text: `${verb} is in progress.` };
    case 'complete': return { tone: 'ok', text: op.kind === 'reactivate' ? 'Reactivated. The sign-ins this change had turned off were restored.' : 'Deactivated. Sign-ins were disabled and open sessions were signed out.' };
    case 'completed-with-exceptions': return { tone: 'warn', text: `${verb} finished with exceptions. Review each app below.` };
    case 'incomplete': return { tone: 'warn', text: op.kind === 'deactivate'
      ? 'Deactivation is NOT finished. The person is already refused at each app, but some sign-in changes did not complete. Retry is safe.'
      : 'Reactivation is NOT finished. Some sign-ins were not restored. Retry is safe.' };
    case 'superseded': return { tone: 'info', text: `An earlier ${op.kind} was replaced by a later change.` };
    default: return { tone: 'warn', text: `${verb}: ${op.state}.` };
  }
}

const DISABLE_TEXT: Record<string, { tone: Tone; text: string }> = {
  pending: { tone: 'info', text: 'Waiting' },
  intent: { tone: 'info', text: 'In progress' },
  confirmed: { tone: 'ok', text: 'Sign-in disabled' },
  failed: { tone: 'error', text: 'Could not be disabled' },
  unknown: { tone: 'warn', text: 'Result unknown. Retry will check before doing anything.' },
  'owned-unconfirmed': { tone: 'warn', text: 'Appears disabled, but this change cannot prove it did it. It will not be turned back on automatically.' },
  'unconfirmed-acknowledged': { tone: 'warn', text: 'The result was never confirmed. A Platform Owner reviewed it and it stays as it is. It will not be turned back on automatically.' },
  'skipped-prior-disabled': { tone: 'info', text: 'Was already disabled. Left exactly as it was.' },
  'skipped-missing-account': { tone: 'info', text: 'No account found in this app.' },
  'skipped-unmanaged': { tone: 'info', text: 'Not managed by this screen.' },
};
const SIGNOUT_TEXT: Record<string, string> = { none: '', intent: 'sign-out in progress', confirmed: 'open sessions signed out', failed: 'sign-out failed', unknown: 'sign-out result unknown', 'unconfirmed-acknowledged': 'sign-out never confirmed (reviewed)' };
const RESTORE_TEXT: Record<string, { tone: Tone; text: string }> = {
  none: { tone: 'info', text: '' },
  intent: { tone: 'info', text: 'Restoring' },
  restored: { tone: 'ok', text: 'Sign-in turned back on' },
  left: { tone: 'info', text: 'Left disabled' },
  failed: { tone: 'error', text: 'Could not be restored' },
  unknown: { tone: 'warn', text: 'Restore result unknown. Retry will check before doing anything.' },
};

export function loginLine(l: LoginLike, kind: Kind | null): { app: string; tone: Tone; text: string } {
  const d = DISABLE_TEXT[l.disable || 'pending'] || { tone: 'warn' as Tone, text: String(l.disable) };
  const parts: string[] = [];
  let tone: Tone = d.tone;
  if (kind === 'reactivate') {
    const r = RESTORE_TEXT[l.restore || 'none'] || { tone: 'warn' as Tone, text: String(l.restore) };
    if (r.text) { parts.push(r.text); tone = r.tone; } else parts.push(d.text);
    if (l.restore === 'left' && l.restoreReason) parts.push(`(${l.restoreReason.replace(/-/g, ' ')})`);
  } else {
    parts.push(d.text);
    const so = SIGNOUT_TEXT[l.signOut || 'none'];
    if (so && l.disable === 'confirmed') parts.push(so);
    if (l.signOut === 'failed' || l.signOut === 'unknown') tone = 'warn';
  }
  if (l.unsettled) { parts.push('a request was sent and no answer came back; it may still take effect'); tone = 'warn'; }
  if (l.discoveredLate) parts.push('(found after the change started)');
  return { app: appLabel(l.app), tone, text: parts.join(' · ') };
}

export function enforcementNote(s: StatusLike | null | undefined): string | null {
  const e = s && s.enforcement;
  if (!e || !s || !s.operation || s.operation.kind !== 'deactivate') return null;
  return e.inEffect
    ? `New requests from this person are refused at Unique HQ, Pricing and Project Manager (they take effect within about ${e.windowSeconds} seconds).`
    : `New requests are refused at each app within about ${e.windowSeconds} seconds (${e.secondsRemaining} seconds left). A request already accepted is not cancelled.`;
}

export function retryOffer(s: StatusLike | null | undefined): { available: boolean; operationId?: string; note: string } {
  const r = s && s.retry;
  if (!s || !r || !s.open) return { available: false, note: '' };
  if (r.available && r.operationId) {
    return { available: true, operationId: r.operationId, note: 'Retry continues this change from where it stopped. It never repeats a step that already succeeded.' };
  }
  if (r.reason === 'worker-active') return { available: false, note: 'Another request is still working on this change. Check again in a minute.' };
  if (r.reason === 'authority-change-in-progress') {
    return { available: false, note: 'A disable, delete or demotion made directly in an app has not reported its result for this person, so the reactivation cannot finish yet. The person stays inactive. Retry opens once that action reports back or a Platform Owner reviews it. Waiting does not clear it.' };
  }
  if (r.reason === 'provider-outcome-unconfirmed') {
    return { available: false, note: 'A request to the sign-in provider was sent and no answer came back, so it may still take effect at any time. Retry stays closed and waiting does not change that. A Platform Owner has to review it with written evidence first. Nothing is repeated, reversed or turned back on in the meantime.' };
  }
  return { available: false, note: '' };
}

/** Direct account actions on this person (disable, delete, demotion) that have not reported a result pause Reactivate. */
export function reservationNote(s: StatusLike | null | undefined): string | null {
  const n = s && s.authorityReservations ? s.authorityReservations.length : 0;
  if (!n) return null;
  return `${n === 1 ? 'An account change' : `${n} account changes`} made directly in an app (disable, delete or demotion) has not reported its result. Reactivate and new admin rights stay paused for this person until it does, or a Platform Owner reviews it. Waiting does not clear it.`;
}

/** A refusal from the server is shown as the server worded it; this only adds a safe fallback. */
export function failureMessage(e: unknown): string {
  const m = e && typeof (e as { message?: unknown }).message === 'string' ? (e as { message: string }).message : '';
  return m || 'The request could not be completed. Nothing was confirmed. Check the status and try again.';
}
